import { useState } from "react";
import { useLocation } from "react-router-dom";
import { complaintsApi } from "@/api/complaints.api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { fillCopy } from "./contribution-kit-copy";
import { TASK_REPORT_REASONS, pickLocalized } from "./task-help.content";
import type { ContributionTaskKey } from "./task-registry";
import { buildTaskReport } from "./task-report";
import { useKitCopy } from "./use-kit-copy";

/**
 * The "Signaler" dialog: a reason picked from the task's list, optional
 * details, then one complaint through complaintsApi.submitComplaint with the
 * page route and the id of the item on screen (see buildTaskReport), and a
 * toast either way.
 */
export function TaskReportDialog({
  task,
  taskLabel,
  itemId,
  open,
  onOpenChange,
}: {
  task: ContributionTaskKey;
  taskLabel: string;
  itemId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { copy, lang } = useKitCopy();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const reasons = TASK_REPORT_REASONS[task].map((reason) => pickLocalized(reason, lang));
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const selected = reason || reasons[0];

  const close = () => {
    onOpenChange(false);
    setReason("");
    setDetails("");
  };

  const send = async () => {
    setSending(true);
    try {
      await complaintsApi.submitComplaint(
        buildTaskReport({
          task,
          taskLabel,
          reason: selected,
          details,
          page: pathname,
          itemId,
          itemLabel: itemId ? fillCopy(copy.reportItem, { id: itemId }) : undefined,
        }),
      );
      toast({ title: copy.reportSent, description: copy.reportSentDesc });
      close();
    } catch {
      toast({ title: copy.reportError, description: copy.reportErrorDesc, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{copy.reportTitle}</DialogTitle>
          <DialogDescription>{fillCopy(copy.reportDescription, { task: taskLabel })}</DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium text-foreground">{copy.reportReason}</legend>
          <RadioGroup value={selected} onValueChange={setReason} className="gap-1">
            {reasons.map((text) => (
              <label key={text} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 text-sm text-foreground hover:bg-surface-2">
                <RadioGroupItem value={text} />
                {text}
              </label>
            ))}
          </RadioGroup>
        </fieldset>
        <label className="block space-y-2">
          <span className="text-sm text-muted-foreground">{copy.reportDetails}</span>
          <Textarea
            value={details}
            maxLength={500}
            onChange={(event) => setDetails(event.target.value)}
            placeholder={copy.reportDetailsPlaceholder}
            className="min-h-[96px]"
          />
        </label>
        <div className="grid gap-2 sm:grid-cols-2">
          <Button type="button" variant="outline" className="h-11" onClick={close} disabled={sending}>
            {copy.reportCancel}
          </Button>
          <Button type="button" className="h-11" onClick={send} disabled={sending}>
            {copy.reportSend}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
